// src/services/factory/createCrudService.js
import { api } from '../api';
import { objectToQueryString } from '../../utils/apiUtils';

/**
 * Creates a standardized CRUD service for an entity type
 *
 * @param {Object} options - Service configuration options
 * @param {string} options.basePath - Base API path for the entity (e.g., '/clients')
 * @param {string} options.entityName - Name of the entity for logging (e.g., 'client')
 * @param {Object} options.fileUploadOptions - Configuration for file uploads
 * @param {Array} options.relatedEntities - Related entity configurations
 * @param {Array} options.nestedCollections - Nested collections configurations
 * @param {Object} options.customEndpoints - Additional custom endpoints
 * @returns {Object} CRUD service methods for the entity
 */
const createCrudService = ({
  basePath,
  entityName,
  fileUploadOptions = {},
  relatedEntities = [],
  nestedCollections = [],
  customEndpoints = {}
}) => {
  // Ensure basePath starts with a slash but doesn't end with one
  const normalizedBasePath = basePath.startsWith('/') ? basePath : `/${basePath}`;

  // Base CRUD operations
  const baseService = {
    /**
     * Get all entities with optional filters
     * @param {Object} params - Query parameters (pagination, filters, search, sort)
     * @returns {Promise} - Promise with response
     */
    getAll: async (params = {}) => {
      const queryString = objectToQueryString(params);
      return api.get(`${normalizedBasePath}${queryString}`);
    },

    /**
     * Get a single entity by ID
     * @param {string} id - Entity ID
     * @param {Object} params - Additional query parameters
     * @returns {Promise} - Promise with response
     */
    getById: async (id, params = {}) => {
      const queryString = objectToQueryString(params);
      return api.get(`${normalizedBasePath}/${id}${queryString}`);
    },

    /**
     * Create a new entity
     * @param {Object} data - Entity data
     * @returns {Promise} - Promise with response
     */
    create: async (data) => {
      return api.post(normalizedBasePath, data);
    },

    /**
     * Update an existing entity
     * @param {string} id - Entity ID
     * @param {Object} data - Updated entity data
     * @returns {Promise} - Promise with response
     */
    update: async (id, data) => {
      return api.put(`${normalizedBasePath}/${id}`, data);
    },

    /**
     * Partially update an entity
     * @param {string} id - Entity ID
     * @param {Object} data - Partial entity data
     * @returns {Promise} - Promise with response
     */
    patch: async (id, data) => {
      return api.patch(`${normalizedBasePath}/${id}`, data);
    },

    /**
     * Delete an entity
     * @param {string} id - Entity ID
     * @param {Object} params - Additional query parameters
     * @returns {Promise} - Promise with response
     */
    delete: async (id, params = {}) => {
      const queryString = objectToQueryString(params);
      return api.delete(`${normalizedBasePath}/${id}${queryString}`);
    },

    /**
     * Update an entity's status
     * @param {string} id - Entity ID
     * @param {string} status - New status value
     * @returns {Promise} - Promise with response
     */
    updateStatus: async (id, status) => {
      return api.put(`${normalizedBasePath}/${id}/status`, { status });
    },

    /**
     * Get entities as a child resource of a parent entity
     * @param {string} parentId - Parent entity ID
     * @param {string} parentBasePath - Parent entity base path
     * @param {Object} params - Query parameters
     * @returns {Promise} - Promise with response
     */
    getFromParent: async (parentId, parentBasePath, params = {}) => {
      const queryString = objectToQueryString(params);
      const parentPath = parentBasePath.endsWith('/') ? parentBasePath.slice(0, -1) : parentBasePath;
      return api.get(`${parentPath}/${parentId}${normalizedBasePath}${queryString}`);
    },

    /**
     * Create an entity as a child resource of a parent entity
     * @param {string} parentId - Parent entity ID
     * @param {string} parentBasePath - Parent entity base path
     * @param {Object} data - Entity data
     * @returns {Promise} - Promise with response
     */
    createForParent: async (parentId, parentBasePath, data) => {
      const parentPath = parentBasePath.endsWith('/') ? parentBasePath.slice(0, -1) : parentBasePath;
      return api.post(`${parentPath}/${parentId}${normalizedBasePath}`, data);
    },

    /**
     * Perform a bulk operation on entities
     * @param {string} operation - Operation name (e.g., 'delete', 'update')
     * @param {Array} ids - Array of entity IDs
     * @param {Object} data - Additional data for the operation
     * @returns {Promise} - Promise with response
     */
    bulk: async (operation, ids, data = {}) => {
      return api.post(`${normalizedBasePath}/bulk/${operation}`, {
        ids,
        ...data
      });
    },

    /**
     * Export entities
     * @param {string} format - Export format (e.g., 'csv', 'xlsx')
     * @param {Object} filters - Filter criteria
     * @returns {Promise} - Promise with response
     */
    export: async (format = 'csv', filters = {}) => {
      const queryParams = {
        format,
        ...filters
      };
      const queryString = objectToQueryString(queryParams);
      return api.get(`${normalizedBasePath}/export${queryString}`);
    },

    /**
     * Search for entities
     * @param {string} query - Search query
     * @param {Object} params - Additional query parameters
     * @returns {Promise} - Promise with response
     */
    search: async (query, params = {}) => {
      const queryParams = {
        ...params,
        q: query
      };
      const queryString = objectToQueryString(queryParams);
      return api.get(`${normalizedBasePath}/search${queryString}`);
    },

    /**
     * Get entity count with optional filters
     * @param {Object} filters - Filter criteria
     * @returns {Promise} - Promise with response
     */
    count: async (filters = {}) => {
      const queryString = objectToQueryString(filters);
      return api.get(`${normalizedBasePath}/count${queryString}`);
    },

    /**
     * Reorder entities
     * @param {Array} orderedIds - Array of entity IDs in the desired order
     * @returns {Promise} - Promise with response
     */
    reorder: async (orderedIds) => {
      return api.post(`${normalizedBasePath}/reorder`, { orderedIds });
    }
  };

  // Add file upload methods if configured
  const fileUploadService = {};

  if (fileUploadOptions.enabled) {
    /**
     * Upload a file for the entity
     * @param {string} id - Entity ID
     * @param {File} file - File to upload
     * @param {Object} additionalData - Additional form data
     * @returns {Promise} - Promise with response
     */
    fileUploadService.uploadFile = async (id, file, additionalData = {}) => {
      const fieldName = fileUploadOptions.fieldName || 'file';
      const endpoint = fileUploadOptions.endpoint || 'file';

      return api.uploadFile(
        `${normalizedBasePath}/${id}/${endpoint}`,
        file,
        fieldName,
        additionalData
      );
    };
  }

  // Add related entities methods (one-to-one or many-to-one relationships)
  const relatedEntitiesService = {};

  relatedEntities.forEach(relation => {
    const relationPath = relation.path || relation.name;

    // Get related entity
    relatedEntitiesService[`get${capitalize(relation.name)}`] = async (id, params = {}) => {
      const queryString = objectToQueryString(params);
      return api.get(`${normalizedBasePath}/${id}/${relationPath}${queryString}`);
    };

    // Set/update related entity
    relatedEntitiesService[`set${capitalize(relation.name)}`] = async (id, relatedId) => {
      return api.put(`${normalizedBasePath}/${id}/${relationPath}`, { id: relatedId });
    };

    // Remove related entity
    if (relation.removable !== false) {
      relatedEntitiesService[`remove${capitalize(relation.name)}`] = async (id) => {
        return api.delete(`${normalizedBasePath}/${id}/${relationPath}`);
      };
    }
  });

  // Add nested collections methods (one-to-many relationships)
  const nestedCollectionsService = {};

  nestedCollections.forEach(collection => {
    const collectionPath = collection.path || collection.name;

    // Get collection
    nestedCollectionsService[`get${capitalize(collection.name)}`] = async (id, params = {}) => {
      const queryString = objectToQueryString(params);
      return api.get(`${normalizedBasePath}/${id}/${collectionPath}${queryString}`);
    };

    // Add item to collection
    nestedCollectionsService[`add${capitalize(collection.singularName || collection.name.slice(0, -1))}`] =
      async (id, itemData) => {
        return api.post(`${normalizedBasePath}/${id}/${collectionPath}`, itemData);
      };

    // Update item in collection
    nestedCollectionsService[`update${capitalize(collection.singularName || collection.name.slice(0, -1))}`] =
      async (id, itemId, itemData) => {
        return api.put(`${normalizedBasePath}/${id}/${collectionPath}/${itemId}`, itemData);
      };

    // Remove item from collection
    nestedCollectionsService[`remove${capitalize(collection.singularName || collection.name.slice(0, -1))}`] =
      async (id, itemId) => {
        return api.delete(`${normalizedBasePath}/${id}/${collectionPath}/${itemId}`);
      };

    // Reorder collection items
    if (collection.orderable) {
      nestedCollectionsService[`reorder${capitalize(collection.name)}`] =
        async (id, orderedIds) => {
          return api.post(`${normalizedBasePath}/${id}/${collectionPath}/reorder`, { orderedIds });
        };
    }
  });

  // Helper method to capitalize first letter
  function capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  // Add custom method for making arbitrary API calls
  const customService = {
    /**
     * Make a custom API call
     * @param {string} path - API path (relative to basePath or absolute)
     * @param {string} method - HTTP method
     * @param {Object} data - Request data
     * @param {Object} queryParams - Query parameters
     * @returns {Promise} - Promise with response
     */
    custom: async (path, method = 'GET', data = null, queryParams = {}) => {
      const queryString = objectToQueryString(queryParams);
      const fullPath = path.startsWith('/') ? path : `${normalizedBasePath}${path}`;
      const url = `${fullPath}${queryString}`;
      
      switch (method.toUpperCase()) {
        case 'GET':
          return api.get(url);
        case 'POST':
          return api.post(url, data);
        case 'PUT':
          return api.put(url, data);
        case 'PATCH':
          return api.patch(url, data);
        case 'DELETE':
          return api.delete(url);
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }
    }
  };

  // Combine all services
  const service = {
    ...baseService,
    ...fileUploadService,
    ...relatedEntitiesService,
    ...nestedCollectionsService,
    ...customService,
    ...customEndpoints
  };

  return service;
};

export default createCrudService;